/* global Config */
// Vendor Libs
import {REHYDRATE} from 'redux-persist';
import {cloneDeep, get, includes, isEmpty, map, now, omit, pick, set, throttle, toLower} from 'lodash';

// Lib App
import actionsServiceCaller from '../lib/app/actions-service-caller';

// Lib Core
import {isEqualJson, parseJson} from '../lib/core/object';

// Configs
import preferencesConfig from '../../config/preferences-config';
import servicesConfig from '../../config/services-config';
import servicesFormConfig from '../../config/services-form-config';

const servicesDataKey = 'users';
const formName = 'editUserPreferences';
let allowSave = false;

const preferencesMiddleware = (store) => (next) => (action) => {
    const {dispatch, getState} = store;

    next(action);

    const nextState = getState();
    const actions = {
        AUTHENTICATE_FETCH_FULFILLED: () => fetchPreferences(dispatch, nextState),
        AUTHENTICATE_LOGOUT: () => resetPreferences(dispatch),
        PREFERENCES_INIT: () => fetchPreferences(dispatch, nextState),
        SEARCH_DATA_FETCH: () => savePreferencesThrottled(dispatch, nextState),
        SEARCH_DATA_RESET: () => savePreferencesThrottled(dispatch, nextState),
        [REHYDRATE]: () => fetchPreferences(dispatch, nextState),
    };

    if (action.updatePreferences) {
        return savePreferencesThrottled(dispatch, nextState);
    }
    if (actions[action.type]) {
        return actions[action.type]();
    }
};

function getStoresResetAuth(jsonStores) {
    const stores = {};

    map(preferencesConfig, (_config, storeKey) => {
        const jsonStore = get(jsonStores, storeKey, {});
        const servicesToOmit = get(preferencesConfig, [storeKey, 'authReset']);

        set(stores, storeKey, servicesToOmit ? omit(jsonStore, servicesToOmit) : jsonStore);
    });

    return stores;
}

function fetchPreferences(dispatch, nextState) {
    const authUser = get(nextState.auth, 'user');
    const user = get(nextState.preferences, 'user');

    if (authUser && (!user || user !== authUser)) {
        const dispatchFetched = (jsonResponse) => {
            const userData = get(jsonResponse, 'content.0');

            if (userData) {
                const {clientId, preferences} = userData;
                const preferencesJson = parseJson(preferences);

                if (preferencesJson && toLower(clientId) === toLower(authUser)) {
                    const stores = Config.base.matureContent
                        ? get(preferencesJson, 'stores', {})
                        : getStoresResetAuth(get(preferencesJson, 'stores', {}));
                    const matureStores = Config.base.matureContent
                        ? getStoresResetAuth(get(preferencesJson, 'matureStores', {}))
                        : get(preferencesJson, 'matureStores', {});

                    dispatch({
                        type: 'PREFERENCES_LOAD',
                        matureStores,
                        stores,
                        user: clientId,
                    });
                }
            }

            dispatch({
                type: 'PREFERENCES_FETCH_FULFILLED',
            });

            allowSave = true;
        };
        const dispatchError = (error) => {
            dispatch({
                type: 'PREFERENCES_FETCH_REJECTED',
                error,
            });
        };
        const actionParams = {
            servicesConfig: servicesConfig[servicesDataKey],
            dispatchFetched,
            dispatchError,
        };
        const queryParams = {
            q: `clientId=="${authUser}"`,
            page: 0,
            size: 1,
        };
        dispatch({
            type: 'PREFERENCES_FETCH',
        });

        return Promise.resolve(actionsServiceCaller(actionParams, queryParams));
    }
    allowSave = true;
}

function resetPreferences(dispatch) {
    allowSave = false;
    dispatch({
        type: 'PREFERENCES_RESET',
    });
}

function savePreferences(dispatch, nextState) {
    const user = get(nextState.auth, 'user');

    if (allowSave && user) {
        let matureStores = {};
        let nextStores = {};
        let prevStores = {};
        let stores = {};

        if (Config.base.matureContent) {
            matureStores = getStoreData(nextState, 'matureStores');
            nextStores = getStoreData(nextState, 'matureStores');
            prevStores = get(nextState.preferences, 'matureStores');
            stores = get(nextState.preferences, 'stores');
        } else {
            matureStores = get(nextState.preferences, 'matureStores');
            nextStores = getStoreData(nextState, 'stores');
            prevStores = get(nextState.preferences, 'stores');
            stores = getStoreData(nextState, 'stores');
        }

        const data = {
            matureStores,
            stores,
            timestamp: now(),
            user: nextState.auth.user,
        };
        const dispatchFetched = () => {
            dispatch({
                type: 'PREFERENCES_SAVE_FULFILLED',
                matureStores,
                stores,
                user,
            });
        };
        const dispatchError = () => {
            dispatch({
                type: 'PREFERENCES_SAVE_REJECTED',
                user,
            });
        };
        const actionParams = {
            servicesConfig: servicesFormConfig[formName],
            dispatchFetched,
            dispatchError,
        };
        const formData = {
            preferences: JSON.stringify(data),
        };

        if (!isEqualJson(nextStores, prevStores)) {
            dispatch({
                type: 'PREFERENCES_SAVE',
                matureStores,
                stores,
                user,
            });

            return actionsServiceCaller(actionParams, formData);
        }
    }
}

function getStoreData(state, storesType) {
    const stores = cloneDeep(get(state.preferences, storesType, {}));

    map(preferencesConfig, (config, storeKey) => {
        const stateData = get(state, storeKey);

        if (stateData && config) {
            if (config.paths) {
                const {paths, whitelist} = config;
                let storeData = get(stores, storeKey, {});

                map(state[storeKey], (data, dataKey) => {
                    if (!whitelist || includes(whitelist, dataKey)) {
                        const saveData = pick(data, paths);

                        if (!isEmpty(saveData)) {
                            set(storeData, dataKey, saveData);
                        }
                    }
                });

                if (whitelist) {
                    storeData = pick(storeData, whitelist);
                }

                if (!isEmpty(storeData)) {
                    set(stores, storeKey, storeData);
                }
            } else {
                set(stores, storeKey, stateData);
            }
        }
    });

    return stores;
}

const savePreferencesThrottled = throttle(savePreferences, 2000, {leading: true, trailing: true});

export default preferencesMiddleware;
